import type { User } from "firebase/auth";
import {
  type DocumentData,
  doc,
  getDocFromServer,
  runTransaction,
} from "firebase/firestore";
import type { SignupAuthMethod, SignupPlatform } from "../constants/accountAcquisition";
import { db } from "../firebase";
import { assertAdultBirthDate, isAdultBirthDate } from "../utils/adultEligibility";

const USERS_COLLECTION = "users";
const DISCLAIMERS_COLLECTION = "disclaimers";

type AccountProfileDocument = {
  id: string;
  userName: string;
  email: string;
  birthDate: Date | null;
  isRacing: boolean;
  didAgreeToTermsAndConditions: boolean;
  registeredRacesIds: string[];
  bookmarkedRacesIds: string[];
  friendUserIds: string[];
  blockedUserIds: string[];
  followingUserIds: string[];
  isBannedFromRacing: boolean;
  gear: unknown[];
  totalDistanceRaced: number;
  totalRacesFinished: number;
  totalRacesWon: number;
  createdRaceIds: string[];
  isAppleTester: boolean;
  registrationDate: Date;
  profilePhotoSetupStatus: "pending";
  signupPlatform: SignupPlatform;
  signupAuthMethod: SignupAuthMethod;
};

export type EnsureAccountProvisionedInput = {
  user: User;
  displayName?: string | null;
  birthDate?: Date | null;
  acceptedAt?: Date;
  signupAuthMethod: SignupAuthMethod;
  acceptedTerms: true;
};

export type AccountReadiness = { complete: boolean; displayName: string; birthDate: Date | null };
const provisioningListeners = new Set<(uid: string) => void>();

const profileBirthDate = (profile: DocumentData | undefined): Date | null => {
  const value = profile?.birthDate;
  return value instanceof Date ? value : value?.toDate?.() ?? null;
};

const assertAccountIdentity = (
  uid: string,
  profile: DocumentData | undefined,
  disclaimer: DocumentData | undefined
): void => {
  if ((profile && profile.id !== uid) || (disclaimer && (
    disclaimer.didAgreeToTerms !== true || disclaimer.user?.id !== uid ||
    disclaimer.user?.didAgreeToTermsAndConditions !== true
  ))) {
    throw Object.assign(new Error("Your account setup needs assistance. Contact support."), {
      code: "auth/account-data-invalid",
    });
  }
};

export type AccountProvisioningResult = {
  profileCreated: boolean;
  disclaimerCreated: boolean;
};

const LEGAL_USER_SNAPSHOT_OPTIONAL_FIELDS = [
  "legalFirstName",
  "legalLastName",
  "birthDate",
  "gender",
  "bio",
  "city",
  "state",
  "country",
  "legalAddress",
  "profilePhotoURLString",
  "emergencyContact",
  "organizerPayoutTermsAcceptedAt",
  "organizerPayoutTermsVersion",
] as const;

const getEmailFallbackName = (email: string): string => {
  const localPart = email.split("@", 1)[0]?.trim();
  return localPart || "RAUXA Athlete";
};

export const buildAccountProfileDocument = (
  input: EnsureAccountProvisionedInput,
  registrationDate: Date
): AccountProfileDocument => {
  const email = input.user.email?.trim() || "";
  const userName =
    input.displayName?.trim() ||
    input.user.displayName?.trim() ||
    getEmailFallbackName(email);

  return {
    id: input.user.uid,
    userName,
    email,
    birthDate: input.birthDate ?? null,
    isRacing: false,
    didAgreeToTermsAndConditions: true,
    registeredRacesIds: [],
    bookmarkedRacesIds: [],
    friendUserIds: [],
    blockedUserIds: [],
    followingUserIds: [],
    isBannedFromRacing: false,
    gear: [],
    totalDistanceRaced: 0,
    totalRacesFinished: 0,
    totalRacesWon: 0,
    createdRaceIds: [],
    isAppleTester: false,
    registrationDate,
    profilePhotoSetupStatus: "pending",
    // This service runs in the web client. A browser on an iOS or Android device
    // is still the web signup surface; native clients write their own constants.
    signupPlatform: "web",
    signupAuthMethod: input.signupAuthMethod,
  };
};

const buildLegalUserSnapshot = (
  storedProfile: DocumentData,
  fallbackProfile: AccountProfileDocument
): DocumentData => {
  // Acquisition metadata describes where the profile was first provisioned. It
  // is operational attribution, not part of the immutable legal acknowledgement.
  const {
    signupPlatform: _signupPlatform,
    signupAuthMethod: _signupAuthMethod,
    profilePhotoSetupStatus: _profilePhotoSetupStatus,
    ...legalFallbackProfile
  } = fallbackProfile;
  const snapshot: DocumentData = {
    ...legalFallbackProfile,
    id:
      typeof storedProfile.id === "string" && storedProfile.id.trim()
        ? storedProfile.id
        : legalFallbackProfile.id,
    userName:
      typeof storedProfile.userName === "string" && storedProfile.userName.trim()
        ? storedProfile.userName
        : legalFallbackProfile.userName,
    email:
      typeof storedProfile.email === "string"
        ? storedProfile.email
        : legalFallbackProfile.email,
  };

  const requiredFallbackKeys = Object.keys(legalFallbackProfile) as Array<
    keyof typeof legalFallbackProfile
  >;
  requiredFallbackKeys.forEach((key) => {
    if (key === "id" || key === "userName" || key === "email") {
      return;
    }
    if (key in storedProfile && storedProfile[key] !== undefined) {
      snapshot[key] = storedProfile[key];
    }
  });

  LEGAL_USER_SNAPSHOT_OPTIONAL_FIELDS.forEach((key) => {
    if (key in storedProfile && storedProfile[key] !== undefined) {
      snapshot[key] = storedProfile[key];
    }
  });

  // Push tokens, active-race locks, Stripe state, and other operational fields do
  // not belong in the immutable legal acknowledgement snapshot.
  // Record today's acknowledgement, not the profile's previous consent flag.
  snapshot.didAgreeToTermsAndConditions = true;
  return snapshot;
};

export const accountProvisioningService = {
  onAccountProvisioned(listener: (uid: string) => void): () => void {
    provisioningListeners.add(listener);
    return () => { provisioningListeners.delete(listener); };
  },
  async getAccountReadiness(uid: string): Promise<AccountReadiness> {
    // Read failures are errors, never evidence that an account is missing.
    const [profile, disclaimer] = await Promise.all([
      getDocFromServer(doc(db, USERS_COLLECTION, uid)),
      getDocFromServer(doc(db, DISCLAIMERS_COLLECTION, uid)),
    ]);
    const data = profile.data();
    assertAccountIdentity(uid, data, disclaimer.data());
    const displayName = typeof data?.userName === "string" ? data.userName.trim() : "";
    const birthDate = profileBirthDate(data);
    return {
      complete: profile.exists() && Boolean(displayName) && isAdultBirthDate(birthDate) &&
        data?.didAgreeToTermsAndConditions === true &&
        disclaimer.exists() && disclaimer.data()?.didAgreeToTerms === true,
      displayName,
      birthDate,
    };
  },
  async ensureAccountProvisioned(
    input: EnsureAccountProvisionedInput
  ): Promise<AccountProvisioningResult> {
    if (input.acceptedTerms !== true) throw new Error("Please accept the terms before continuing.");
    const acceptedAt = input.acceptedAt || new Date();
    const fallbackProfile = buildAccountProfileDocument(input, acceptedAt);
    const userRef = doc(db, USERS_COLLECTION, input.user.uid);
    const disclaimerRef = doc(db, DISCLAIMERS_COLLECTION, input.user.uid);

    const result = await runTransaction(db, async (transaction) => {
      const [userSnapshot, disclaimerSnapshot] = await Promise.all([
        transaction.get(userRef),
        transaction.get(disclaimerRef),
      ]);
      const profileCreated = !userSnapshot.exists();
      const disclaimerCreated = !disclaimerSnapshot.exists();
      assertAccountIdentity(input.user.uid, userSnapshot.data(), disclaimerSnapshot.data());
      // Preserve an existing declaration; a signup retry cannot replace a minor's
      // date with an adult date. Missing legacy dates are collected explicitly.
      const birthDate = profileBirthDate(userSnapshot.data()) ?? input.birthDate;
      assertAdultBirthDate(birthDate);
      const resolvedProfile = { ...fallbackProfile, birthDate };
      const storedProfile: DocumentData = profileCreated
        ? resolvedProfile
        : { ...userSnapshot.data(), birthDate };

      if (profileCreated) {
        transaction.set(userRef, resolvedProfile);
      } else {
        const repairs: DocumentData = {};
        if (!profileBirthDate(userSnapshot.data())) repairs.birthDate = birthDate;
        if (storedProfile.didAgreeToTermsAndConditions !== true) repairs.didAgreeToTermsAndConditions = true;
        if (typeof storedProfile.userName !== "string" || !storedProfile.userName.trim()) repairs.userName = fallbackProfile.userName;
        if (Object.keys(repairs).length) transaction.update(userRef, repairs);
      }

      if (disclaimerCreated) {
        transaction.set(disclaimerRef, {
          user: buildLegalUserSnapshot(storedProfile, resolvedProfile),
          date: acceptedAt,
          didAgreeToTerms: true,
        });
      }

      return { profileCreated, disclaimerCreated };
    });
    provisioningListeners.forEach((listener) => {
      try { listener(input.user.uid); } catch { /* A UI observer cannot undo a committed account. */ }
    });
    return result;
  },
};
